import { normalizeEmail } from '@helpers/normalizeEmail';
import { useAPI } from '@providers/useAPI';
import { useQueryParams } from '@providers/useQueryParams';
import { useMutation } from '@tanstack/react-query';
import { createContext, ReactNode, useContext, useRef, useState } from 'react';
import { recoverPassword } from '../api/recoverPassword';
import { getPasswordRecoveryError } from '../helpers/getPasswordRecoveryError';

type TState = {
  isComplete: boolean;
  isLoading: boolean;
  email: string;
  setEmail: (v: string) => void;
  error: string;
  setError: (v: string) => void;
  recovery: () => void;
  resetForm: () => void;
};

const PageContext = createContext<TState>({
  isComplete: false,
  isLoading: false,
  email: '',
  setEmail: () => null,
  error: '',
  setError: () => null,
  recovery: () => null,
  resetForm: () => null,
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryParams = useQueryParams();
  const [email, setEmail] = useState(() => normalizeEmail(queryParams.get('email') ?? ''));
  const [error, setError] = useState('');
  const isRecoveryInFlight = useRef(false);

  const recoveryMutation = useMutation({
    mutationFn: (recoveryEmail: string) => recoverPassword({ call, email: recoveryEmail }),
    onError: (mutationError) => {
      setError(getPasswordRecoveryError(mutationError));
    },
    onSettled: () => {
      isRecoveryInFlight.current = false;
    },
  });

  const recovery = () => {
    if (isRecoveryInFlight.current) {
      return;
    }
    isRecoveryInFlight.current = true;
    setError('');
    recoveryMutation.mutate(normalizeEmail(email));
  };

  const resetForm = () => {
    recoveryMutation.reset();
    setError('');
  };

  return (
    <PageContext.Provider
      value={{
        isComplete: recoveryMutation.isSuccess,
        email,
        setEmail,
        setError,
        isLoading: recoveryMutation.isPending,
        error,
        recovery,
        resetForm,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
